import { Reference, useApolloClient, useReactiveVar } from "@apollo/client";
import filter from "lodash/filter";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import some from "lodash/some";
import { useCallback } from "react";
import {
  GetRooms2Output,
  RoomOutput2,
  RoomStatus,
} from "~@/graphql/codegen/generated";
import useInternalUser from "~@/hooks/useInternalUser";
import { newRoomsIdsVar } from "~@/reactive-variables";

const useRemoveChatRooms = () => {
  const newRoomsIds = useReactiveVar(newRoomsIdsVar);
  const { cache } = useApolloClient();

  const { internalUser } = useInternalUser();

  const shouldRemove = useCallback(
    (room: RoomOutput2) => {
      const onlyFromOtherTeams = !some(
        internalUser?.teams?.map((team) => team?.id),
        (teamId) => {
          return teamId ? room.teamsIds.includes(teamId) : false;
        }
      );

      return (
        (room.previousStatus === RoomStatus.NonResponded &&
          (room.status === RoomStatus.Accepted ||
            (room.status === RoomStatus.NonAccepted && onlyFromOtherTeams) ||
            room.status === RoomStatus.Completed)) ||
        (room.previousStatus === RoomStatus.NonAccepted &&
          (room.status === RoomStatus.Accepted ||
            (room.status === RoomStatus.NonAccepted && onlyFromOtherTeams) ||
            room.status === RoomStatus.Completed)) ||
        (room.previousStatus === RoomStatus.Accepted &&
          (room.status === RoomStatus.NonAccepted ||
            (room.status === RoomStatus.Accepted &&
              room.activeOperator?.id !== internalUser?.id) ||
            room.status === RoomStatus.Completed)) ||
        room.previousStatus === RoomStatus.Completed ||
        room.previousStatus === RoomStatus.Invisible ||
        ((room.previousStatus === RoomStatus.Accepted ||
          room.previousStatus === RoomStatus.NonAccepted ||
          room.previousStatus === RoomStatus.NonResponded) &&
          room.status === RoomStatus.Invisible)
      );
    },
    [internalUser]
  );

  const removeChatRooms = useCallback(
    (chatRooms: RoomOutput2[]) => {
      const filteredChatRooms = filter(chatRooms, (room) => {
        return shouldRemove(room);
      });

      const groupedRooms = groupBy(
        filteredChatRooms,
        (room) => room.previousStatus
      );

      for (const [previousStatus, rooms] of Object.entries(groupedRooms)) {
        if (rooms.length > 0) {
          const roomsIdsToRemove = flatMap(rooms, (room) =>
            room.linkedRooms.map((linkedRoom) => linkedRoom.id)
          );

          cache.modify({
            fields: {
              getRooms2(existing: GetRooms2Output | null, { readField }) {
                if (existing?.requestedStatus === previousStatus) {
                  const existingRoomsRefs = readField<Reference[]>(
                    "rooms",
                    existing
                  );
                  const leadIds = rooms.map((roomData) => roomData.leadId);
                  const isRoomInExisting = Boolean(
                    existing.rooms?.find((roomRef) => {
                      const leadId = readField(
                        "leadId",
                        roomRef as unknown as Reference
                      );
                      return leadIds.includes(leadId as number);
                    })
                  );
                  if (existingRoomsRefs) {
                    return {
                      ...existing,
                      rooms: filter(existingRoomsRefs, (ref) => {
                        const roomId = readField<string>("id", ref);

                        return roomId
                          ? !roomsIdsToRemove.includes(roomId)
                          : true;
                      }),
                      pagination: {
                        ...existing.pagination,
                        totalItems:
                          (existing.pagination?.totalItems || 0) -
                          (isRoomInExisting ? 1 : 0),
                      },
                    };
                  }

                  return existing;
                }

                return existing;
              },
            },
          });

          if (previousStatus === RoomStatus.NonAccepted) {
            newRoomsIdsVar(
              filter(newRoomsIds, (id) => {
                return !roomsIdsToRemove.includes(id);
              })
            );
          }
        }
      }
    },
    [cache, newRoomsIds, shouldRemove]
  );

  return removeChatRooms;
};

export default useRemoveChatRooms;
